import type { Region, Vec2 } from '../utils/geometry';
import type { TerrainHeightMapDocument } from './TerrainSculpting';

export interface CourseDocumentPoint3 {
  x: number;
  y?: number;
  z: number;
}
export interface TeeBoxDocument {
  id: string;
  name: string;
  position: CourseDocumentPoint3;
}
export interface PinDocument {
  id: string;
  label?: string;
  position: CourseDocumentPoint3;
}
export interface RegionDocument {
  id: string;
  region: Region;
  waterLevel?: number;
}
export interface PlacementDocument {
  id: string;
  kind: 'tree' | 'rock';
  position: CourseDocumentPoint3;
  radius?: number;
  rotationY?: number;
  scale?: number;
}
export interface HoleDocument {
  id: string;
  number: number;
  par: number;
  yardage: number;
  name: string;
  teeBoxes: TeeBoxDocument[];
  pins: PinDocument[];
  activePinIndex: number;
  fairways: RegionDocument[];
  greens: RegionDocument[];
  roughRegions: RegionDocument[];
  bunkers: RegionDocument[];
  waterRegions: RegionDocument[];
  vegetation: PlacementDocument[];
  props: PlacementDocument[];
}
export interface CourseDocument {
  schemaVersion: 2;
  id: string;
  name: string;
  description: string;
  environmentPreset: string;
  terrain?: TerrainHeightMapDocument;
  holes: HoleDocument[];
  /** Optional production metadata. Editor-authored courses remain backwards compatible. */
  production?: import('./data/courseAuthoring').CourseProductionMetadata;
}

export function createEmptyCourseDocument(): CourseDocument {
  return {
    schemaVersion: 2,
    id: 'new-course',
    name: 'New Course',
    description: '',
    environmentPreset: 'afternoon',
    holes: [],
  };
}

export function createEmptyHoleDocument(number: number): HoleDocument {
  return {
    id: `hole-${number}`,
    number,
    par: 4,
    yardage: 400,
    name: `Hole ${number}`,
    teeBoxes: [],
    pins: [],
    activePinIndex: 0,
    fairways: [],
    greens: [],
    roughRegions: [],
    bunkers: [],
    waterRegions: [],
    vegetation: [],
    props: [],
  };
}

export function point2(point: CourseDocumentPoint3): Vec2 {
  return { x: point.x, z: point.z };
}
export function cloneCourseDocument(doc: CourseDocument): CourseDocument {
  return JSON.parse(JSON.stringify(doc)) as CourseDocument;
}
export function newEditorId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
